<script lang="ts">
  import clsx from 'clsx';
  import { Card } from '@athena-lib/Card';
  import { Image } from '@athena-lib/Image';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { BaseHeading, Paragraph, Text } from '@athena-lib/Text';
  import { setContainerContextWidth } from '@athena-lib/container-context.svelte';
  import { parseHeadingLevel } from '@athena-lib/Text/BaseHeading/types';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import { Button } from '@athena-lib/Button';
  import { ArrowForward } from '@athena-lib/Icons';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { CardMedia, IconCardProps } from '../types';

  const {
    title,
    description,
    details,
    titleHeadingTag,
    image,
    class: className,
    hideDescriptionInMobile = true,
    useMaxHeight = false,
    horizontalOnMobile = false,
    horizontal = false,
    flip = false,
    images = [],
    preHeader = '',
    textAlign = 'text-center',
    onContinue,
    onFlip,
    ...props
  }: IconCardProps = $props();
  const translations = getCommonTranslations();

  const iconCardClasses = $derived.by(() => {
    if (flip) {
      return clsx(className);
    }
    if (horizontalOnMobile) {
      return clsx('flex-col transition-transform hover:scale-105', className);
    } else {
      return clsx(
        'min-h-full w-auto transition-transform hover:scale-105',
        useMaxHeight && 'max-h-96',
        className,
      );
    }
  });
  setContainerContextWidth({
    sm: 0.5,
    md: 0.5,
    lg: 0.5,
  });

  const aspectRatio = horizontalOnMobile || horizontal || flip ? 'auto' : '4/5';

  const whatImageToUse = () => {
    if (images.length > 0) {
      return images;
    } else if (image) {
      return [image];
    } else {
      return [];
    }
  };
</script>

{#snippet clickableImage(file: CardMedia)}
  <a type="button" href={props.link?.href}>
    <Image
      loading={'lazy'}
      src={file.url}
      alt={file.alt ?? ''}
      imgClass={clsx(flip ? 'h-full object-cover' : '')}
    />
  </a>
{/snippet}

{#snippet badge()}
  <div
    class={clsx(
      props.badgeBackgroundClass,
      'absolute right-4 top-5 max-w-48 pl-2 pr-2 text-center',
    )}
  >
    <Text element="p" class="truncate" color={props.badgeTextColor ?? 'light'} bold={true}
      >{props.badgeText}</Text
    >
  </div>
{/snippet}

<Card
  {...props}
  link={flip ? undefined : props.link}
  padding={flip ? 'none' : 'medium'}
  class={clsx(iconCardClasses)}
  aspect={aspectRatio}
>
  {#if props.badgeText && props.badgeText !== ''}
    {@render badge()}
  {/if}
  {#if horizontalOnMobile || horizontal}
    <div class="flex aspect-[3/1] flex-row items-center">
      <div class="mr-4 flex aspect-square max-w-28 items-center object-contain">
        {#if flip && props.link}
          {@render clickableImage(whatImageToUse()[0])}
        {:else}
          <Image
            loading={'lazy'}
            src={whatImageToUse()[0].url}
            alt={whatImageToUse()[0].alt ?? ''}
            imgClass={clsx(flip ? 'h-full object-cover' : '')}
          />
        {/if}
      </div>
      <Stack space="small" class="justify-center">
        {#if preHeader}
          <Grid columns={{ sm: 2, md: 12, lg: 12 }} space="small">
            <GridItem colSpan={{ sm: 1, md: 8, lg: 8 }}>
              <StackItem class="mb-0 text-left leading-snug">
                <Paragraph
                  class="line-clamp-1"
                  size={{ sm: 'xsmall', md: 'medium', lg: 'medium' }}
                  color="medium">{preHeader}</Paragraph
                >
              </StackItem>
            </GridItem>
            <GridItem colSpan={{ sm: 1, md: 4, lg: 4 }}>
              <StackItem class="mb-0 text-right leading-snug">
                <button
                  type="button"
                  class="cursor-pointer text-st-blue-500 underline"
                  aria-label="Flip card details"
                  onclick={() => onFlip?.()}
                >
                  {details}
                </button>
              </StackItem>
            </GridItem>
          </Grid>
        {/if}
        {#if title}
          <StackItem>
            <BaseHeading
              level={parseHeadingLevel(titleHeadingTag) ?? 3}
              size={flip
                ? { sm: 'small', md: 'medium', lg: 'medium' }
                : { sm: 'small', md: 'xsmall', lg: 'xsmall' }}
            >
              {title}
            </BaseHeading>
          </StackItem>
        {/if}
        {#if description}
          <StackItem>
            <Paragraph class="line-clamp-2">{description}</Paragraph>
          </StackItem>
        {/if}
        {#if details && !flip}
          <StackItem>
            <Paragraph>{details}</Paragraph>
          </StackItem>
        {/if}
        {#if flip}
          <Grid columns={{ sm: 2, md: 2, lg: 2 }} space="small" class="pt-[24px]">
            <GridItem>
              {#if onContinue}
                <Button color={props.buttonColor} class="mb-2" onClick={() => onContinue()}
                  >{translations.quickStart}</Button
                >
              {/if}
            </GridItem>
            <GridItem class="text-right">
              <Button
                size="circle"
                color="white"
                full={false}
                link={props.link}
                element="a"
                ariaLabel="Read more about this material"
              >
                <ArrowForward sizeClass={'w-8 h-8'}></ArrowForward>
              </Button>
            </GridItem>
          </Grid>
        {/if}
      </Stack>
    </div>
  {:else}
    <Stack space="small" class="flex h-full">
      <StackItem class={clsx('flex aspect-square items-center justify-center')}>
        <div
          class={clsx('mx-auto flex', flip ? 'h-full' : 'w-[80%]', 'justify-center object-contain')}
        >
          {#if flip && props.link}
            {@render clickableImage(whatImageToUse()[0])}
          {:else}
            <Image
              loading={'lazy'}
              src={whatImageToUse()[0].url}
              alt={whatImageToUse()[0].alt ?? ''}
              imgClass={clsx(flip ? 'h-full object-cover' : '')}
            />
          {/if}
        </div>
      </StackItem>
      <StackItem>
        <Stack space="small" class={clsx(flip ? 'p-5' : '')}>
          {#if preHeader}
            <Grid columns={{ sm: 2, md: 12, lg: 12 }} space="small">
              <GridItem colSpan={{ sm: 1, md: 8, lg: 8 }}>
                <StackItem class="mb-0 text-left leading-snug">
                  <Paragraph
                    class="line-clamp-1"
                    size={{ sm: 'small', md: 'medium', lg: 'medium' }}
                    color="medium">{preHeader}</Paragraph
                  >
                </StackItem>
              </GridItem>
              <GridItem colSpan={{ sm: 1, md: 4, lg: 4 }}>
                <StackItem class="mb-0 text-right leading-snug">
                  <button
                    type="button"
                    class="cursor-pointer text-st-blue-500 underline"
                    aria-label="Flip card details"
                    onclick={() => onFlip?.()}
                  >
                    {details}
                  </button>
                </StackItem>
              </GridItem>
            </Grid>
          {/if}
          {#if title}
            <StackItem class={clsx('mb-0 leading-snug', textAlign)}>
              <BaseHeading
                level={parseHeadingLevel(titleHeadingTag) ?? 3}
                size={flip
                  ? { sm: 'medium', md: 'medium', lg: 'medium' }
                  : { sm: 'small', md: 'xsmall', lg: 'xsmall' }}
              >
                {title}
              </BaseHeading>
            </StackItem>
          {/if}
          <StackItem class={clsx('block', textAlign)}>
            <Paragraph class="line-clamp-2">{description}</Paragraph>
          </StackItem>
          {#if details && !flip}
            <StackItem class={clsx(hideDescriptionInMobile && 'hidden md:block', textAlign)}>
              <Paragraph>{details}</Paragraph>
            </StackItem>
          {/if}
        </Stack>
      </StackItem>
      {#if flip}
        <StackItem class="absolute inset-x-3 bottom-6 md:inset-x-5">
          <Grid columns={{ sm: 2, md: 12, lg: 12 }} space="gutter">
            <GridItem colSpan={{ sm: 1, md: 6, lg: 6 }}>
              {#if onContinue}
                <Button color={props.buttonColor} onClick={() => onContinue()}
                  >{translations.quickStart}</Button
                >
              {/if}
            </GridItem>
            <GridItem colSpan={{ sm: 1, md: 6, lg: 6 }}>
              <div class="flex justify-end">
                <Button
                  color="white"
                  class="flex !h-14 !min-w-14 items-center justify-center !rounded-full !px-0"
                  link={props.link}
                  element="a"
                  ariaLabel="Read more about this material"
                >
                  <ArrowForward sizeClass={'w-6 h-6'}></ArrowForward>
                </Button>
              </div>
            </GridItem>
          </Grid>
        </StackItem>
      {/if}
    </Stack>
  {/if}
</Card>
